import { getPresignedPreviewUrl, Location } from '@/api/documents'
import { Flex, Tooltip } from '@/components'
import { Typography } from '@/components/atoms/Typography'
import { useSnackbar } from '@/providers'
import { Dot } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { TableCell } from './styles'

type PreviewCellProps = {
  companyId: string
  fileId: string
  displayName: string
  servedAt: string | null
}

const updateFileInLocations = (locations: Location[], targetId: string) =>
  locations.map((loc) => ({
    ...loc,
    files: loc.files.map((file) =>
      file.id === targetId
        ? { ...file, servedAt: new Date().toISOString() }
        : file,
    ),
  }))

const TRUNCATE_AFTER_CHARACTERS = 60

const PreviewCell = ({
  companyId,
  fileId,
  displayName,
  servedAt: initServedAt,
}: PreviewCellProps) => {
  const { t } = useTranslation()
  const snackbar = useSnackbar()
  const queryClient = useQueryClient()

  const [isServed, setIsServed] = useState(!!initServedAt)

  const handleClick = async () => {
    try {
      const response = await getPresignedPreviewUrl(companyId, fileId)
      const url = response.data.url
      window.open(url, '_blank', 'noopener,noreferrer')

      setIsServed(true)

      queryClient.setQueriesData<Record<string, Location[]> | undefined>(
        ['documents', companyId],
        (oldData) =>
          oldData
            ? {
                ...oldData,
                files: updateFileInLocations(oldData.files, fileId),
              }
            : oldData,
      )
    } catch (error) {
      snackbar.error(t('snackbars.error.failedToGetPresignedPreviewUrl'))
      console.error(error)
    }
  }

  // Only show tooltip if the displayName is longer than 60 characters
  if (displayName.length > TRUNCATE_AFTER_CHARACTERS) {
    return (
      <TableCell width="464px">
        <Tooltip content={displayName}>
          <Flex alignItems="center">
            {!isServed && <Dot size={32} color="blue" />}
            <Typography ellipsis variant="p2Body" onClick={handleClick}>
              {displayName.slice(0, TRUNCATE_AFTER_CHARACTERS)}...
            </Typography>
          </Flex>
        </Tooltip>
      </TableCell>
    )
  }

  return (
    <TableCell width="464px">
      {!isServed && <Dot size={32} color="blue" />}
      <Typography ellipsis variant="p2Body" onClick={handleClick}>
        {displayName}
      </Typography>
    </TableCell>
  )
}

export default PreviewCell
